import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import Tooltip from '../molecules/Tooltip';
import css from './Tabs.css';
import { tabsShape } from './types';
import Icon, { IconSizes, IconVariants } from '../foundations/Icon';

const Tabs = ({
    tabs = [],
    selected = 'regular',
    containerStyle = {},
    dividerStyle = {},
    onClick = () => {},
    containerClass = '',
    tabClass = '',
    activeTabClass = '',
    tabLabelContainerClass = '',
    dividerClass = '',
    dataTestId = '',
    iconSize = IconSizes.LARGE,
    numbered = false,
    disabled = false,
}) => {
    const handleClick = (e, tabId) => {
        e.preventDefault();
        e.stopPropagation();
        onClick(tabId);
    };

    const getTab = (tab, index) => {
        const isActiveTab = tab.id === selected;

        const tabElement = (
            <button
                type="button"
                className={classNames(tabClass, css.tab, {
                    [css.hidden]: tab.hidden,
                    [css.disabled]: disabled,
                    [css.activeTab]: isActiveTab,
                    [activeTabClass]: activeTabClass && isActiveTab,
                })}
                onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleClick(e, tab.id);
                }}
                key={tab.id}
                disabled={disabled}
            >
                <div style={tab.style} className={classNames(tabLabelContainerClass, css.tabLabelContainer)}>
                    {tab.icon && <tab.icon className={css.tabIcon} />}
                    {tab.iconName && (
                        <Icon
                            name={tab.iconName}
                            variant={isActiveTab ? IconVariants.ACTIVE : IconVariants.DARK}
                            size={iconSize}
                        />
                    )}
                    {numbered && (
                        <div className={classNames(css.tabNumber, { [css.activeTabNumber]: isActiveTab })}>
                            {index + 1}
                        </div>
                    )}
                    <div className={classNames(css.tabLabel, tab.tabLabelClass)}>
                        <Translate id={tab.label} data={tab.labelData} />
                    </div>
                </div>
            </button>
        );

        return tab.tooltipText ? (
            <Tooltip key={tab.id} className={css.toolTip} titleTranslationKey={tab.tooltipText}>
                {tabElement}
            </Tooltip>
        ) : (
            tabElement
        );
    };

    return (
        tabs.length > 0 && (
            <div
                className={classNames(css.container, containerClass)}
                style={{
                    ...containerStyle,
                }}
                data-testid={dataTestId}
            >
                {tabs.map(getTab)}
                <div className={classNames(css.divider, dividerClass)} style={dividerStyle} />
            </div>
        )
    );
};

Tabs.propTypes = {
    tabs: PropTypes.arrayOf(PropTypes.shape(tabsShape)),
    selected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onClick: PropTypes.func,
    containerClass: PropTypes.string,
    tabClass: PropTypes.string,
    activeTabClass: PropTypes.string,
    tabLabelContainerClass: PropTypes.string,
    containerStyle: PropTypes.objectOf(PropTypes.any),
    dividerStyle: PropTypes.objectOf(PropTypes.any),
    dividerClass: PropTypes.string,
    dataTestId: PropTypes.string,
    iconSize: PropTypes.oneOf(Object.values(IconSizes)),
    numbered: PropTypes.bool,
    disabled: PropTypes.bool,
};

export default Tabs;
